import type {
  GlobalFilters,
  QueryResult,
  ScenarioColumn,
  ScenarioComparisonView,
  ScenarioMetricCell,
} from '@aurora-pvi/contracts';
import { ROUTES, buildPath } from '@aurora-pvi/contracts';
import type { Scenario, ScenarioMetric } from '@aurora-pvi/seed';
import { money, round } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, canApprove, resolved } from '../context';
import { applyFilters } from '../filters';
import { buildMetadata } from '../metadata';
import { simpleRef } from '../refs';

/**
 * Scenario comparison (ref: 09_…png).
 *
 * `formula`, `formulaVersion` and `inputs` travel on every cell. A scenario
 * number nobody can take apart is a number nobody will act on, and the cost of
 * carrying the derivation is trivial next to the cost of a finance director
 * refusing to sign.
 */

export function createScenarioRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  const cellFrom = (
    metric: ScenarioMetric,
    baseline: ScenarioMetric | undefined,
  ): ScenarioMetricCell => {
    const isCurrency = metric.unit === 'currency';
    const value =
      metric.value === null
        ? metric.textValue
        : isCurrency
          ? money(Math.round(metric.value * 100))
          : metric.value;

    const delta =
      baseline && baseline.value !== null && metric.value !== null && baseline.value !== 0
        ? round((metric.value - baseline.value) / Math.abs(baseline.value), 4)
        : null;

    return {
      metricId: metric.metricId,
      label: metric.label,
      value,
      unit: metric.unit,
      deltaVsBaseline: delta,
      deltaLabel:
        delta === null ? null : `${delta > 0 ? '+' : ''}${round(delta * 100, 1)}% vs current state`,
      formula: metric.formula,
      formulaVersion: metric.formulaVersion,
      inputs: metric.inputs,
      confidence: metric.confidence,
      trend: null,
    };
  };

  const columnFrom = (scenario: Scenario, baseline: Scenario | undefined): ScenarioColumn => ({
    scenarioId: scenario.id,
    type: scenario.type,
    title: scenario.title,
    subtitle: scenario.subtitle,
    colorToken: scenario.colorToken,
    version: scenario.version,
    status: scenario.status,
    isDirty: scenario.isDirty,
    isReadOnly: scenario.isReadOnly || !canApprove(user),
    orgLayers: scenario.orgLayers,
    averageSpan: scenario.averageSpan,
    managementRatio: scenario.managementRatio,
    sections: scenario.sections.map((section) => {
      const baselineSection = baseline?.sections.find((s) => s.sectionId === section.sectionId);
      return {
        sectionId: section.sectionId,
        title: section.title,
        cells: section.metrics.map((metric) =>
          cellFrom(
            metric,
            baselineSection?.metrics.find((m) => m.metricId === metric.metricId),
          ),
        ),
      };
    }),
    implementationMoves: scenario.implementationMoves,
  });

  return {
    async compare(
      scenarioId: string,
      filters: GlobalFilters,
    ): Promise<QueryResult<ScenarioComparisonView>> {
      const scope = applyFilters(dataset, index, user, filters);
      const baseline = dataset.scenarios.find((s) => s.type === 'Current');
      const requested =
        dataset.scenarios.find((s) => s.id === scenarioId || s.businessCode === scenarioId) ??
        dataset.scenarios.find((s) => s.type === 'TargetProductModel');

      const columns = dataset.scenarios.map((scenario) => columnFrom(scenario, baseline));

      const target = requested ?? dataset.scenarios[1];
      const missingBaselineReasons: string[] = [];
      if (!baseline) missingBaselineReasons.push('No approved current-state scenario exists.');
      if (target && target.lastRecalculatedAt === null) {
        missingBaselineReasons.push(
          `${target.title} has never been calculated. Assumptions have changed since the last run.`,
        );
      }
      if (!user.canSeeRestrictedFinance) {
        missingBaselineReasons.push(
          'Financial rows are restricted for your role and are shown as unavailable.',
        );
      }

      const impactSummary: ScenarioMetricCell[] = target
        ? target.sections
            .flatMap((section) => section.metrics)
            .filter((metric) => ['SM-002', 'SM-010', 'SM-012', 'SM-020'].includes(metric.metricId))
            .map((metric) =>
              cellFrom(
                metric,
                baseline?.sections
                  .flatMap((section) => section.metrics)
                  .find((m) => m.metricId === metric.metricId),
              ),
            )
        : [];

      const view: ScenarioComparisonView = {
        subject: simpleRef(
          dataset.enterprise.id,
          'Enterprise',
          dataset.enterprise.businessCode,
          dataset.enterprise.name,
          ROUTES.portfolioHierarchy,
        ),
        timeHorizonLabel: `FY${dataset.fiscalYear}–FY${dataset.fiscalYear + 2}`,
        currency: dataset.currency,
        assumptions: (target ?? dataset.scenarios[0])?.assumptions ?? [],
        columns,
        scenarioHealth: {
          financial: user.canSeeRestrictedFinance ? 'Medium' : 'Unknown',
          delivery: 'Medium',
          risk: 'Low',
          feasibility: 'Medium',
          overallConfidencePercent: user.canSeeRestrictedFinance ? 62 : 41,
        },
        impactSummary,
        differences: [
          {
            text: 'Application estate falls by 418 systems through consolidation.',
            isPositive: true,
          },
          {
            text: 'Cost that cannot be attributed to a product falls from 23% to 6%.',
            isPositive: true,
          },
          { text: 'Management layers reduce from eight to five.', isPositive: true },
          {
            text: 'Transition cost of roughly 12% of annual run is incurred in year one.',
            isPositive: false,
          },
          { text: 'AI uptake is the least evidenced assumption in the model.', isPositive: false },
        ],
        recommendation: target
          ? {
              scenarioId: target.id,
              title: `Proceed with ${target.title}`,
              rationale:
                'The target product model delivers most of the benefit with assumptions rated Medium confidence. The AI-enabled variant should be revisited once the current use cases have twelve months of evidence behind them.',
              confidence: 'Medium',
            }
          : null,
        lastRecalculatedAt: target?.lastRecalculatedAt ?? null,
        missingBaselineReasons,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: buildPath(ROUTES.scenarioCompare, { scenarioId: target?.id ?? 'current' }),
          sources: PAGE_SOURCES.scenarios,
          confidence: 'Medium',
          actions: [
            { action: 'recalculate', permission: 'edit', enabled: canApprove(user) },
            { action: 'export', permission: 'export', enabled: true },
            {
              action: 'approveScenario',
              permission: 'approve',
              enabled: false,
              disabledReason: 'Scenario approval sits with the portfolio board.',
            },
          ],
        }),
      });
    },
  };
}
